// calcExpression.ts — Auswertung eines eingetippten Ausdrucks für den
// wissenschaftlichen Taschenrechner. Bewusst OHNE `eval` und OHNE `new Function`:
// die Eingabe kommt aus einem freien Textfeld, wäre dort also fremder Code mit
// vollem Zugriff auf die laufende Sitzung (Cookie, Bearer-Token, DOM). Zusätzlich
// blockiert eine strenge CSP (`script-src` ohne 'unsafe-eval') beides zur Laufzeit
// — der Rechner würde damit schlicht nicht funktionieren. Stattdessen drei Stufen:
// Tokenizer → Shunting-Yard (Infix → RPN) → RPN-Auswertung.
//
// ZAHLENFORMAT (deutsch): Das Komma ist der Dezimaltrenner. Punkte innerhalb einer
// Ziffernfolge gelten als Tausenderpunkte und werden ENTFERNT — „1.200.000" ist
// 1200000. Diese Regel gilt ausnahmslos, „1.5" ist also fünfzehn und NICHT 1,5.
// Bewusst keine Heuristik nach Gruppengröße: „1.200" wäre sonst mehrdeutig
// (1200 vs. 1,2), und im deutschen Format ist es eindeutig 1200. Wer 1,5 meint,
// tippt das Komma.
//
// Weitere Festlegungen: Winkelfunktionen rechnen im BOGENMASS (wie Math.sin),
// `log` ist der Zehner-, `ln` der natürliche Logarithmus. Das Postfix-`%` teilt
// schlicht durch 100 (kein Bezug auf einen linken Summanden wie bei manchen
// Tischrechnern, wo „200+10%" 220 ergibt). Wissenschaftliche Notation („1e5")
// gibt es nicht — `e` ist die eulersche Zahl. Implizite Multiplikation („2π",
// „2(3+4)") ebenfalls nicht: das wäre geraten, nicht gerechnet.

export type CalcResult = { ok: true; value: number } | { ok: false; error: string };

/** Die vier Fehlertexte, die der Rechner anzeigt — mehr Fälle gibt es nicht. */
const FEHLER = {
  divisionDurchNull: "Division durch 0",
  klammern: "Klammern stimmen nicht",
  syntax: "Ungültiger Ausdruck",
  keinErgebnis: "Kein Ergebnis",
} as const;

type FehlerText = (typeof FEHLER)[keyof typeof FEHLER];

/** Interner Abbruch. Nach außen wird daraus ein `{ ok: false }` (siehe unten). */
class CalcError extends Error {}

function fail(text: FehlerText): never {
  throw new CalcError(text);
}

type OpKey = "+" | "-" | "*" | "/" | "^" | "neg" | "%";
type FnKey = "sin" | "cos" | "tan" | "ln" | "log" | "sqrt";

type Token =
  | { kind: "num"; value: number }
  | { kind: "op"; op: OpKey }
  | { kind: "fn"; fn: FnKey }
  | { kind: "lparen" }
  | { kind: "rparen" };

/**
 * Präzedenz und Assoziativität. `prefix` markiert das unäre Minus: Präfix-
 * Operatoren (und Funktionen) werden im Shunting-Yard OHNE Pop-Schleife gestapelt,
 * denn links von ihnen kann nie ein fertiger Operand stehen. Andernfalls würde
 * „2^-3" beim `neg` das `^` vom Stapel holen, dem dann der zweite Operand fehlt.
 */
const OPS: Record<OpKey, { prec: number; rightAssoc: boolean; arity: 1 | 2; prefix?: boolean }> = {
  "+": { prec: 1, rightAssoc: false, arity: 2 },
  "-": { prec: 1, rightAssoc: false, arity: 2 },
  "*": { prec: 2, rightAssoc: false, arity: 2 },
  "/": { prec: 2, rightAssoc: false, arity: 2 },
  neg: { prec: 3, rightAssoc: true, arity: 1, prefix: true },
  "^": { prec: 4, rightAssoc: true, arity: 2 }, // rechts-assoziativ → 2^3^2 = 2^9
  "%": { prec: 5, rightAssoc: false, arity: 1 }, // Postfix
};

/** Funktionen binden am stärksten. */
const FN_PREC = 6;

const FUNCS: Record<FnKey, (x: number) => number> = {
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  ln: Math.log,
  log: Math.log10,
  sqrt: Math.sqrt,
};

const CONSTS: Record<string, number> = { pi: Math.PI, e: Math.E };

const isDigit = (c: string | undefined): boolean => !!c && c >= "0" && c <= "9";
const isLetter = (c: string | undefined): boolean => !!c && /[a-z]/i.test(c);

/** Stufe 1: Zeichenkette → Tokens. */
function tokenize(src: string): Token[] {
  const out: Token[] = [];
  // Steht als Nächstes ein Wert an (Ausdrucksanfang, nach „(" oder nach einem
  // Operator)? Genau dann ist ein „-" ein Vorzeichen — und ein „*" ein Fehler.
  let expectValue = true;
  let i = 0;

  while (i < src.length) {
    const c = src[i];

    if (/\s/.test(c)) {
      i++;
      continue;
    }

    // Zahl — auch mit führendem Komma („,5"). Punkte werden mitgelesen und beim
    // Normalisieren verworfen (Tausenderpunkte, siehe Dateikopf).
    if (isDigit(c) || (c === "," && isDigit(src[i + 1]))) {
      if (!expectValue) fail(FEHLER.syntax);
      let raw = "";
      while (isDigit(src[i]) || src[i] === ".") raw += src[i++];
      if (src[i] === ",") {
        raw += src[i++];
        while (isDigit(src[i])) raw += src[i++];
      }
      const value = Number(raw.replace(/\./g, "").replace(",", "."));
      if (!Number.isFinite(value)) fail(FEHLER.syntax);
      out.push({ kind: "num", value });
      expectValue = false;
      continue;
    }

    // Funktionsname oder Konstante
    if (isLetter(c) || c === "π") {
      let name = "";
      if (c === "π") {
        name = "pi";
        i++;
      } else {
        while (isLetter(src[i])) name += src[i++];
      }
      const key = name.toLowerCase();
      if (!expectValue) fail(FEHLER.syntax);
      if (key in FUNCS) {
        // Die Funktion braucht selbst noch einen Operanden → expectValue bleibt true.
        out.push({ kind: "fn", fn: key as FnKey });
        continue;
      }
      if (key in CONSTS) {
        out.push({ kind: "num", value: CONSTS[key] });
        expectValue = false;
        continue;
      }
      fail(FEHLER.syntax);
    }

    if (c === "(") {
      if (!expectValue) fail(FEHLER.syntax);
      out.push({ kind: "lparen" });
      i++;
      continue;
    }

    if (c === ")") {
      if (expectValue) fail(FEHLER.syntax); // „(2+)" — der Klammer fehlt der Inhalt
      out.push({ kind: "rparen" });
      i++;
      continue;
    }

    if (c === "%") {
      if (expectValue) fail(FEHLER.syntax); // Postfix: braucht einen Operanden links
      out.push({ kind: "op", op: "%" });
      i++;
      continue;
    }

    if (c === "+" || c === "-" || c === "*" || c === "/" || c === "^") {
      if (expectValue) {
        if (c === "-") out.push({ kind: "op", op: "neg" });
        else if (c !== "+") fail(FEHLER.syntax); // „*", „/", „^" ohne linken Operanden
        // Ein „+" als Vorzeichen ist wirkungslos und wird verworfen.
        i++;
        continue;
      }
      out.push({ kind: "op", op: c });
      expectValue = true;
      i++;
      continue;
    }

    fail(FEHLER.syntax);
  }

  return out;
}

const precOf = (t: Token): number =>
  t.kind === "fn" ? FN_PREC : t.kind === "op" ? OPS[t.op].prec : 0;

/** Stufe 2: Shunting-Yard — Infix-Tokens → umgekehrte polnische Notation. */
function toRpn(tokens: Token[]): Token[] {
  const out: Token[] = [];
  const stack: Token[] = [];

  for (const tok of tokens) {
    if (tok.kind === "num") {
      out.push(tok);
      continue;
    }
    if (tok.kind === "fn" || tok.kind === "lparen") {
      stack.push(tok);
      continue;
    }
    if (tok.kind === "rparen") {
      while (stack.length && stack[stack.length - 1].kind !== "lparen") out.push(stack.pop()!);
      if (!stack.length) fail(FEHLER.klammern);
      stack.pop();
      continue;
    }

    const op = OPS[tok.op];
    if (!op.prefix) {
      while (stack.length) {
        const top = stack[stack.length - 1];
        if (top.kind === "lparen") break;
        const topPrec = precOf(top);
        if (topPrec > op.prec || (topPrec === op.prec && !op.rightAssoc)) out.push(stack.pop()!);
        else break;
      }
    }
    stack.push(tok);
  }

  while (stack.length) {
    const top = stack.pop()!;
    if (top.kind === "lparen") fail(FEHLER.klammern);
    out.push(top);
  }
  return out;
}

function applyBinary(op: OpKey, a: number, b: number): number {
  switch (op) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    case "/":
      // Vor der Division geprüft, damit der eigene Fehlertext greift und nicht
      // erst am Ende das unspezifische „Kein Ergebnis" (Infinity).
      if (b === 0) fail(FEHLER.divisionDurchNull);
      return a / b;
    case "^":
      return Math.pow(a, b);
    default:
      fail(FEHLER.syntax);
  }
}

/** Stufe 3: RPN auswerten. Ein leerer oder unvollständiger Stapel = Syntaxfehler. */
function evalRpn(rpn: Token[]): number {
  const stack: number[] = [];

  for (const tok of rpn) {
    if (tok.kind === "num") {
      stack.push(tok.value);
      continue;
    }
    if (tok.kind === "fn") {
      const x = stack.pop();
      if (x === undefined) fail(FEHLER.syntax);
      stack.push(FUNCS[tok.fn](x));
      continue;
    }
    if (tok.kind !== "op") fail(FEHLER.syntax); // Klammern gibt es in RPN nicht mehr

    if (OPS[tok.op].arity === 1) {
      const x = stack.pop();
      if (x === undefined) fail(FEHLER.syntax);
      stack.push(tok.op === "neg" ? -x : x / 100);
      continue;
    }
    const b = stack.pop();
    const a = stack.pop();
    if (a === undefined || b === undefined) fail(FEHLER.syntax);
    stack.push(applyBinary(tok.op, a, b));
  }

  if (stack.length !== 1) fail(FEHLER.syntax);
  return stack[0];
}

/**
 * Wertet einen eingetippten Ausdruck aus. Wirft NIE — jeder Fehler kommt als
 * `{ ok: false, error }` mit einem der vier Texte aus {@link FEHLER} zurück.
 */
export function evaluateExpression(src: string): CalcResult {
  try {
    const value = evalRpn(toRpn(tokenize(src)));
    // sqrt(-1), ln(-1), 2^99999 … — rechnerisch gibt es kein Ergebnis.
    if (!Number.isFinite(value)) fail(FEHLER.keinErgebnis);
    return { ok: true, value };
  } catch (err) {
    // Alles Unerwartete (z. B. ein RangeError) wird zum Syntaxfehler — nach außen
    // darf aus diesem Modul keine Exception dringen.
    return { ok: false, error: err instanceof CalcError ? err.message : FEHLER.syntax };
  }
}
